/**
 *  Custom tags added for application-internal use. In general we should
 *  lean on and migrate towards using RTK-generated tags wherever possible
 *  and reserve these for cases where we really, truly need to roll our
 *  own caching behavior.
 *
 *  @deprecated prefer RTK-generated tags wherever possible.
 */
const CUSTOM_TAGS = {
  AUDIENCE_DATA: 'AudienceData',
  CROSSTABS_CSV: 'CrosstabsCsv',
  BLUEPRINTS_CSV: 'BlueprintsCSV',
  FILE_ANSWER: 'FileAnswer',
  ORG: 'Org',
  PRESENTATION_RULES: 'PresentationRules',
  PRODUCT_GROUPS: 'ProductGroups',
  PRODUCT_HANDLING_PREFERENCE: 'ProductHandlingPreference',
  QUALITY_CONTROL_RESULTS_RESPONSES: 'QualityControlResultsResponses',
  RECRUITING_CRITERIA: 'RecruitingCriteria',
  RECRUITING_DATA: 'RecruitingData',
  SHOW_LOGIC_ALLOWED_OPS: 'ShowLogicAllowedOps',
  RESULTS_CSV: 'ResultsCSV',
  RESULTS_CODED_CSV: 'ResultsCodedCSV',
  RESULTS_CSV_ALL_RESPONDENTS: 'ResultsCSVAllRespondents',
  RESULTS_CODED_CSV_ALL_RESPONDENTS: 'ResultsCodedCSVAllRespondents',
  RESULTS_TEXT: 'ResultsText',
  RESULTS_SCORECARD_ENTRIES: 'ResultsScorecardEntries',
  SEGMENT_COUNTS: 'SegmentCount',
  FEASIBILITY_ESTIMATE: 'FeasibilityEstimate',
  EXPLORE_RESULTS: 'ExploreResults',
  CROSSTABS_DATA: 'CrosstabsData',
  SUMMARIES: 'Summaries',
  SUBSCRIPTION: 'Subscription',
  HUBSPOT_DEAL: 'HubSpotDeal',
} as const;

/**
 *  Legacy mapping of constants to RTK-generated tags. If you're able
 *  to remove an item and replace with the RTK-generated tag, please do!
 *
 *  @deprecated reference {@link addTagTypes} instead
 */
const GENERATED_TAGS = {
  ADMIN: 'Admins',
  BLUEPRINT: 'Blueprints',
  CUSTOMER: 'Customers',
  INSERT: 'Inserts',
  KIT: 'Kits',
  MODULE: 'Modules',
  MODULE_CONFIGURATION: 'ModuleConfigurations',
  NOTIFICATION_UNSUBSCRIPTION: 'NotificationUnsubscriptions',
  PRODUCT: 'Products',
  PRODUCT_PROJECT_ASSETS: 'ProductProjectAssets',
  PROJECT: 'Projects',
  PROJECT_CONFIGURATION: 'ProjectsConfiguration',
  PROJECT_ATTACHMENT: 'Attachments',
  PROJECT_LINK: 'Links',
  PROJECT_SURVEY_CONFIG: 'SurveyConfigs',
  PROJECT_CATEGORIES: 'ProjectCategories',
  QUALITY_CONTROL_RESULTS: 'QualityControlReport',
  RESULT_CONTEXT: 'ResultContexts',
  RESULTS_META: 'ResultsMeta',
  SHIPMENT: 'Shipments',
  SOLUTION: 'Solutions',
  SOLUTION_SURVEY_DESIGN: 'SolutionSurveyDesigns',
  SURVEY_BUNDLES: 'SurveyBundles',
  SURVEY_CONFIG_BLOCKS: 'SurveyConfigBlocks',
  SURVEY_CONFIG_BLOCK_GROUPS: 'SurveyConfigBlockGroups',
  SURVEY_CONFIG_QUESTION_ROUTING: 'SurveyConfigRouting',
  SURVEY_GROUPS: 'SurveyGroups',
  SURVEY_QUESTIONS: 'SurveyQuestions',
  SEGMENTS: 'Segment',
  TEAM: 'Teams',
  TEST_CATEGORIES: 'TestCategories',
  TEST_CLASSIFICATIONS: 'TestClassifications',
} as const;

/**
 *  @deprecated reference RTK-generated tags instead
 */
export const TAG_TYPES = { ...GENERATED_TAGS, ...CUSTOM_TAGS } as const;

/** Cache tags invalidated when any results-affecting data changes (segments, survey groups, etc.) */
export const RESULTS_INVALIDATION_TAGS = [
  TAG_TYPES.EXPLORE_RESULTS,
  TAG_TYPES.CROSSTABS_DATA,
  TAG_TYPES.SUMMARIES,
] as const;

/**
 * The chat-conversation *list*'s own cache identity, distinct from any one conversation's id.
 *
 * Refreshing the list has to target this rather than a bare `{ type: 'ChatConversations' }`: RTK
 * resolves an id-less tag through `Object.values(provided).flat()`, so the bare form matches every
 * id under the type and would refetch whichever conversation's transcript is currently open — the
 * largest payload in the feature — on every completed turn.
 */
export const CHAT_CONVERSATIONS_LIST_TAG = {
  type: 'ChatConversations',
  id: 'LIST',
} as const;

/**
 * Persona chat conversation list sentinel — same rationale as {@link CHAT_CONVERSATIONS_LIST_TAG}:
 * invalidate this rather than a bare `{ type: 'PersonaChatConversations' }` so a completed turn
 * does not refetch the open transcript.
 */
export const PERSONA_CHAT_CONVERSATIONS_LIST_TAG = {
  type: 'PersonaChatConversations',
  id: 'LIST',
} as const;
